/**
 * @file Saga updating one active-contract charge template's payment route via
 * the dedicated paidToOwner endpoint; the body carries the three-way
 * `paymentRouteId` plus `isCollective` (`firstDate` deliberately unsent).
 *
 * Flow: GLOBAL_CHARGE_TEMPLATE_UPDATE_PAID_DIRECTLY_TO_OWNER_START →
 * `PATCH charge-templates/:id/paidToOwner` → unwraps `res.data.data` (returns
 * early when empty) → factory →
 * put(GLOBAL_CHARGE_TEMPLATE_UPDATE_PAID_DIRECTLY_TO_OWNER_SUCCESS) + snackbar
 * → `onSuccess`.
 * Exceptions: catch → ErrorHandlerSequence(err, action) →
 * put(GLOBAL_CHARGE_TEMPLATE_UPDATE_PAID_DIRECTLY_TO_OWNER_FAILURE).
 */
import { call, put, takeEvery } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import { ErrorHandlerSequence } from "../Sequences/ErrorHandlerSequence";
import Http from "../../Http/Http";
import ChargeTemplateFactory from "../../Entity/Factories/ChargeTemplateFactory";
import { ActionInterface } from "../../Interface/ActionInterface";
import { ChargeTemplateInterface } from "../../Entity/Entities/Charge/ChargeTemplateInterface";
import Translator from "../../Translation/Translator";

export default function* chargeTemplateUpdatePaidDirectlyToOwner(action: ActionInterface): any {
  try {
    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_CLEAR
    });

    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_PAID_DIRECTLY_TO_OWNER_REQUEST
    });

    const res = yield call(Http.patch, `charge-templates/${action.payload?.id}/paidToOwner`, {
      paymentRouteId: action.payload?.paymentRouteId,
      isCollective: action.payload?.isCollective
    });

    if (Object.keys(res?.data?.data || {}).length === 0) {
      return;
    }

    const chargeTemplate: ChargeTemplateInterface = ChargeTemplateFactory.create(res.data.data);

    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_PAID_DIRECTLY_TO_OWNER_SUCCESS,
      payload: {
        chargeTemplate: chargeTemplate
      }
    });

    yield put({
      type: Constants.SNACKBAR_ADD_MESSAGE,
      payload: {
        message: Translator.trans("snackbar-message-charge-template-updated-successfully"),
        type: "success"
      }
    });

    if (action.hasOwnProperty("onSuccess") === true) {
      action.onSuccess.call(this, chargeTemplate);
    }
  } catch (err) {
    yield ErrorHandlerSequence(err, action);

    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_PAID_DIRECTLY_TO_OWNER_FAILURE,
      payload: {
        err: err
      }
    });
  }
}

export function* watchChargeTemplateUpdatePaidDirectlyToOwner() {
  yield takeEvery(Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_PAID_DIRECTLY_TO_OWNER_START, chargeTemplateUpdatePaidDirectlyToOwner);
}
